import React from "react";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import StudioButton from "./StudioButton";
import Icon from "./Icon";
import authorPhoto from "../assets/author.jpg";
import { ECO_URLS, trackEcoClick } from "../lib/ecoTrack";

/**
 * Контент-блоки лендинга. Текст на русском, все блоки с scroll-reveal анимацией.
 * Порядок на главной (см. HomePage):
 *   фичи → редактор → скины → приложение → алмазы → альфа → экосистема → автор.
 * Скриншоты — из public/screenshots/.
 */

/* ── Блок «Бесплатная платформа» ──────────────────────────────────── */
export function FeaturesBlock() {
  const items = [
    {
      icon: "gamepad",
      title: "Играй бесплатно",
      text: "Все игры платформы доступны бесплатно. На ПК играй в браузере на сайте Майнкрафтии, на телефоне — через приложение.",
    },
    {
      icon: "wrench",
      title: "Создавай бесплатно",
      text: "Свой 3D-редактор Рублокс, скрипты на JavaScript и бесплатные вводные курсы — собери свою игру.",
    },
    {
      icon: "noDonate",
      title: "Без доната",
      text: "Внутри игр нет покупок за реальные деньги. Скины и приколы — за алмазы, которые зарабатываются обучением.",
    },
  ];
  return (
    <section className="rb-section rb-section--alt rb-section--glow">
      <div className="rb-container">
        <Reveal as="span" className="rb-section__eyebrow">
          Почему Рублокс
        </Reveal>
        <Reveal as="h2" className="rb-section__title">
          Полностью <span className="rb-section__title-accent">бесплатная</span>{" "}
          платформа
        </Reveal>
        <Reveal as="p" className="rb-section__subtitle">
          Никаких платных подписок на игры и никакого доната внутри игр.
        </Reveal>
        <div className="rb-features">
          {items.map((it, i) => (
            <Reveal
              key={it.title}
              className="rb-feature"
              variant="up"
              delay={i * 110}
            >
              <div className="rb-feature__icon" aria-hidden>
                <Icon name={it.icon} size={32} />
              </div>
              <h3 className="rb-feature__title">{it.title}</h3>
              <p className="rb-feature__text">{it.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Редактор игр» ──────────────────────────────────────────── */
export function EditorBlock() {
  return (
    <section className="rb-section" id="editor">
      <div className="rb-container">
        <div className="rb-split">
          <Reveal className="rb-split__content" variant="left">
            <div className="rb-split__eyebrow">Редактор Рублокс</div>
            <h2 className="rb-split__title">
              Создавай свои 3D-игры в браузере
            </h2>
            <p className="rb-split__text">
              Игры для Рублокса собираются в{" "}
              <strong>3D-редакторе Рублокс</strong> — он работает прямо в
              браузере на сайте онлайн-школы «Майнкрафтия». Бесплатно.
            </p>
            <ul className="rb-split__list">
              <li>Воксельный и гладкий ландшафт, блоки, примитивы, модели</li>
              <li>Скрипты на JavaScript — полноценная логика игры</li>
              <li>Бесплатные вводные курсы, чтобы быстро начать</li>
              <li>Опубликовал игру — она попадает в каталог Рублокса</li>
            </ul>
            {/* openStudio делает сетевой запрос за SSO-тикетом и редиректит.
                Раньше промис не ожидался и не имел catch: на медленной сети
                кнопка выглядела мёртвой несколько секунд, человек кликал
                повторно и выпускал лишние тикеты, а при ошибке попадал
                в Студию гостем, не понимая почему. */}
            <StudioButton />
          </Reveal>
          <Reveal className="rb-split__media" variant="right" delay={120}>
            <img
              src="/screenshots/shot1.jpg"
              alt="3D-редактор Рублокс — воксельный ландшафт"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Скины» ─────────────────────────────────────────────────── */
export function SkinsBlock() {
  return (
    <section className="rb-section rb-section--alt" id="skins">
      <div className="rb-container">
        <div className="rb-split">
          <Reveal className="rb-split__media" variant="left">
            <img
              src="/screenshots/shot9.jpg"
              alt="Выбор скинов персонажа в Рублоксе"
            />
            <span className="rb-split__media-badge">Новое</span>
          </Reveal>
          <Reveal className="rb-split__content" variant="right" delay={120}>
            <div className="rb-split__eyebrow">Скины персонажа</div>
            <h2 className="rb-split__title">
              Десятки скинов для твоего&nbsp;аватара
            </h2>
            <p className="rb-split__text">
              В Рублокс <strong>добавлены скины</strong> — настраивай внешний
              вид персонажа на свой вкус. Меняй облик в один клик прямо в
              приложении.
            </p>
            <ul className="rb-split__list">
              <li>Десятки готовых скинов — от классики до ярких образов</li>
              <li>3D-превью: крути персонажа и выбирай</li>
              <li>Скины покупаются за алмазы, без реальных денег</li>
              <li>Дальше — больше: образы, одежда, аксессуары, эмоции</li>
            </ul>
            <Link to="/download" className="rb-btn rb-btn--primary rb-btn--lg">
              Скачать и примерить
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Приложение» ────────────────────────────────────────────── */
export function AppBlock() {
  // 2026-05-24: Windows-сборка отложена — кнопка теперь «Скоро»,
  // как и iOS. Android пока остаётся активной (если apk реально есть).
  const platforms = [
    {
      icon: "windows",
      name: "Windows",
      status: "Доступно",
      ok: true,
      btn: "Скачать для Windows",
    },
    {
      icon: "android",
      name: "Android",
      status: "Доступно",
      ok: true,
      btn: "Скачать APK",
    },
    {
      icon: "apple",
      name: "iOS",
      status: "Скоро",
      ok: false,
      btn: "В разработке",
    },
  ];
  return (
    <section className="rb-section rb-section--glow" id="app">
      <div className="rb-container">
        <Reveal as="span" className="rb-section__eyebrow">
          Приложение
        </Reveal>
        <Reveal as="h2" className="rb-section__title">
          Где запускать игры
        </Reveal>
        <Reveal as="p" className="rb-section__subtitle">
          На ПК играй прямо в браузере на сайте онлайн-школы{" "}
          <a
            href="https://minecraftia-school.ru/kubikon"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#3357FF", textDecoration: "underline" }}
          >
            Майнкрафтия
          </a>{" "}
          — никакой установки не нужно. На телефоне и планшете — скачай
          мобильное приложение ниже.
        </Reveal>
        <div className="rb-platforms">
          {platforms.map((p, i) => (
            <Reveal
              key={p.name}
              className={`rb-platform ${p.ok ? "" : "rb-platform--soon"}`}
              variant="scale"
              delay={i * 110}
            >
              <div className="rb-platform__icon" aria-hidden>
                <Icon name={p.icon} size={38} />
              </div>
              <div className="rb-platform__name">{p.name}</div>
              <div
                className={`rb-platform__status rb-platform__status--${
                  p.ok ? "ok" : "soon"
                }`}
              >
                {p.status}
              </div>
              {p.ok ? (
                <Link
                  to="/download"
                  className="rb-btn rb-btn--primary rb-btn--block"
                >
                  {p.btn}
                </Link>
              ) : (
                <button
                  className="rb-btn rb-btn--ghost rb-btn--block"
                  disabled
                >
                  {p.btn}
                </button>
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Алмазы» ────────────────────────────────────────────────── */
export function DiamondsBlock() {
  return (
    <section className="rb-section rb-section--alt" id="diamonds">
      <div className="rb-container">
        <div className="rb-split rb-split--reverse">
          <Reveal className="rb-split__content" variant="right">
            <div className="rb-split__eyebrow">Внутренняя валюта</div>
            <h2 className="rb-split__title">
              Алмазы — за знания, а не за деньги
            </h2>
            <p className="rb-split__text">
              В играх Рублокса нет покупок за реальные деньги. Скины и приколы
              покупаются за <strong>алмазы</strong> — а алмазы зарабатываются
              обучением в онлайн-школе «Майнкрафтия».
            </p>
            <ul className="rb-split__list">
              <li>
                Решай задачи с автопроверкой — 35 курсов по математике,
                информатике и программированию
              </li>
              <li>Тренажёры с теорией, без учителей и вебинаров</li>
              <li>За решённые задачи начисляются алмазы</li>
              <li>Трать алмазы на скины и приколы в играх</li>
            </ul>
            <Link to="/signup" className="rb-btn rb-btn--primary rb-btn--lg">
              Создать аккаунт
            </Link>
          </Reveal>
          <Reveal className="rb-split__media" variant="left" delay={120}>
            <img
              src="/screenshots/shot13.jpg"
              alt="Курсы программирования онлайн-школы Майнкрафтия"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Альфа-версия» ──────────────────────────────────────────── */
export function AlphaBlock() {
  return (
    <section className="rb-section" id="alpha">
      <div className="rb-container">
        <Reveal className="rb-alpha" variant="scale">
          <div className="rb-alpha__glow" aria-hidden />
          <div className="rb-alpha__badge">
            <span className="rb-alpha__badge-dot" />
            Альфа-версия
          </div>
          <h2 className="rb-alpha__title">
            Платформа в активной разработке
          </h2>
          <p className="rb-alpha__text">
            Рублокс — это <strong>альфа-версия</strong>. Каждый день ведётся
            активная разработка: новые игры, фичи, скины и улучшения. Платформа
            рабочая, но может содержать баги и недоработки.
          </p>
          <p className="rb-alpha__text">
            На каждой странице игры и в редакторе есть кнопка отправки
            баг-репорта. <strong>Сделаем платформу лучше вместе</strong> —
            отправляйте отчёты об ошибках, если находите их.
          </p>
          <div className="rb-alpha__chips">
            <span className="rb-alpha__chip">
              <Icon name="bug" size={18} />
              Кнопка баг-репорта в игре
            </span>
            <span className="rb-alpha__chip">
              <Icon name="refresh" size={18} />
              Ежедневные обновления
            </span>
            <span className="rb-alpha__chip">
              <Icon name="rocket" size={18} />
              Новые фичи каждую неделю
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ── Блок «Экосистема» — единый аккаунт на три продукта ───────────── */
export function EcosystemBlock() {
  return (
    <section className="rb-section rb-section--alt">
      <div className="rb-container">
        <div className="rb-split">
          <Reveal className="rb-split__content" variant="left">
            <div className="rb-split__eyebrow">Экосистема</div>
            <h2 className="rb-split__title">
              Один аккаунт — три платформы
            </h2>
            <p className="rb-split__text">
              Рублокс — часть большой экосистемы. С тем же аккаунтом ты можешь:
            </p>
            <ul className="rb-split__list">
              <li>
                <strong>Майнкрафтия</strong> — решать задачи по программированию
                и математике, зарабатывать алмазы и учиться
              </li>
              <li>
                <strong>Рублокс</strong> — играть в игры с друзьями и создавать
                свои в 3D-редакторе
              </li>
              <li>
                <strong>Маська</strong> — общаться, делать HTML5-игры и видео,
                поддерживать любимых авторов
              </li>
            </ul>
            <p className="rb-split__text">
              Заработал алмазы в Майнкрафтии — тратишь на скины в Рублоксе.
              Вход везде общий, ничего заново регистрировать не нужно.
            </p>
            <a
              href={ECO_URLS.minecraftia}
              target="_blank"
              rel="noreferrer"
              data-mtrk-click="eco:minecraftia:landing_block"
              onClick={() => trackEcoClick("minecraftia", "landing_block")}
              className="rb-btn rb-btn--ghost rb-btn--lg"
            >
              Перейти в «Майнкрафтию»
            </a>
          </Reveal>
          <Reveal className="rb-split__media" variant="right" delay={120}>
            <img
              src="/screenshots/shot12.jpg"
              alt="Главная страница онлайн-школы Майнкрафтия"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Маська» — соцсеть экосистемы ───────────────────────────── */
export function MaskaBlock() {
  return (
    <section className="rb-section" id="maska">
      <div className="rb-container">
        <div className="rb-split rb-split--reverse">
          <Reveal className="rb-split__content" variant="right">
            <div className="rb-split__eyebrow">Ещё в экосистеме</div>
            <h2 className="rb-split__title">
              Маська — общение, игры и видео
            </h2>
            <p className="rb-split__text">
              Маська — социальная сеть экосистемы. Тот же аккаунт, что в
              Рублоксе. Здесь можно:
            </p>
            <ul className="rb-split__list">
              <li>Общаться в чатах и каналах с друзьями</li>
              <li>Смотреть ленту видео и коротких роликов</li>
              <li>Создавать свои HTML5-игры прямо в приложении</li>
              <li>Поддерживать любимых авторов и разработчиков</li>
            </ul>
            <a
              href={ECO_URLS.maska}
              target="_blank"
              rel="noreferrer"
              data-mtrk-click="eco:maska:landing_block"
              onClick={() => trackEcoClick("maska", "landing_block")}
              className="rb-btn rb-btn--primary rb-btn--lg"
            >
              Открыть «Маську»
            </a>
          </Reveal>
          <Reveal className="rb-split__media" variant="left" delay={120}>
            <img
              src="/screenshots/maska-games.jpg"
              alt="Маська — чаты, лента видео и каталог HTML5-игр"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ── Блок «Об авторе» ─────────────────────────────────────────────── */
export function AuthorBlock() {
  return (
    <section className="rb-section rb-section--glow" id="about">
      <div className="rb-container">
        <Reveal as="h2" className="rb-section__title">
          Об авторе
        </Reveal>
        <Reveal className="rb-author" variant="up">
          <div className="rb-author__avatar">
            <img src={authorPhoto} alt="Иванков Максим" />
          </div>
          <div className="rb-author__info">
            <h3 className="rb-author__name">Иванков Максим</h3>
            <p className="rb-author__text">
              Один разработчик, создавший и онлайн-школу программирования и
              математики «Майнкрафтия», и игровую платформу Рублокс как часть
              её экосистемы. От 3D-редактора Рублокс и приложений до курсов с
              автопроверкой — весь проект делается одним человеком.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
